import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  List,
  Wrapper,
  SubTitle
} from './styled';
import {
  OptionChannel,
  ChannelsPublicationShimmer,
  fetchTypePublication,
  TypePublicationModel,
  fetchPrint,
  clearPrintSummaryId,
  FormActions,
  clearExtrachargeAll,
  selectExtracharge,
  setPrintData,
  setTypePublicationSelected,
  fetchUserMessage,
  clearPhotoVignetteAll
} from '@app/src/views/StepTwo/channelPublication';
import { ModalErrorFeature } from '@app/src/common/modalError';
import { isClientEnv } from '@app/src/utils/validations';
import { CATEGORIES, CATEGORY, CATEGORY_ID } from '@app/src/config/categories';
import { Title } from '@app/src/common/Title';
import { cleanAllDateSelected } from '@app/src/common/datePicker/state/calendar/actions';
import { configCalendarId, newspaperIdGroup } from './constants';
import { formatPrice } from '@app/src/utils/normalize';
import { OptionChannelSummary } from '@app/src/views/StepTwo/channelPublication/components/OptionChannelSummary';
import { clearPriceTotal, fetchFreeExtracharge } from '@app/src/views/StepOne/form';

const isClient = isClientEnv();
const EXTRA_WORD_ALIAS = 'palabra-extra';

export const ChannelsPublication: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [isNextStep, setIsNextStep] = useState(false);
  const [publicationTitle, setPublicationTitle] = useState([]);

  const publications = useSelector(state => state.typePublication.data);
  const isFetching = useSelector(state => state.typePublication.isFetching);
  const error = useSelector(state => state.typePublication.error);
  const printData = useSelector(state => state.print);
  const printsByChannel = useSelector(state => state.print.printsByChannel);
  const summaryId = useSelector(state => state.print.summaryId);
  const print = isClient && JSON.parse(sessionStorage.getItem('print'));
  const extracharges = useSelector(state => state.extracharge.data);
  const photoVignette = useSelector(state => state.photoVignette.data);
  const [isShowChannels, setIsShowChannels] = useState(true);
  const [currentNewspaper, setCurrentNewspaper] = useState(null);
  const printIsLoading = useSelector(state => state.print.isFetching);
  const priceTotal = useSelector(state => state.priceTotal);
  const priceTotalData = useSelector(state => state.priceTotal.data);
  const [extraSelected, setExtraSelected] = useState(null);
  const [priceSelected, setPriceSelected] = useState(null);
  const currentChannel: TypePublicationModel = useSelector(state => state.typePublication.selected);
  const currentCalendar = useSelector(state => state.calendar);
  const categoryId = useSelector(state => state.print.general.category);
  const operationId = useSelector(state => state.print.general.operation);
  const propertyId = useSelector(state => state.print.general.property);
  const sectionId = useSelector(state => state.print.general.section);
  const typeVehicleId = useSelector(state => state.print.general.typeVehicle);
  const categoryConfig = CATEGORIES[CATEGORY[categoryId]];
  const userMessageData = useSelector(state => state.userMessage.data);
  const extraWords = useSelector(state => state.print.general.extraWords);

  useEffect(() => {
    if (categoryId) {
      if (print && print.general && categoryConfig && print.general.category === categoryConfig.id && print.general.operation) {
        setExtraSelected(print.extracharges);
        if (print && print.priceTotal && print.priceTotal.data) {
          setPriceSelected(print.priceTotal.data);
        }
        dispatch(fetchFreeExtracharge(categoryId));
        switch (categoryId) {
          case CATEGORY_ID.INMUEBLE:
            dispatch(fetchTypePublication(categoryId, operationId, propertyId));
            break;
          case CATEGORY_ID.EMPLEO:
            dispatch(fetchTypePublication(categoryId));
            break;
          case CATEGORY_ID.EDUCACION:
            dispatch(fetchTypePublication(categoryId, operationId));
            break;
          case CATEGORY_ID.OPORTUNIDAD:
            dispatch(fetchTypePublication(categoryId, operationId, sectionId));
            break;
          case CATEGORY_ID.VEHICULO:
            dispatch(fetchTypePublication(categoryId, operationId, typeVehicleId));
            break;
          default:
            break;
        }
      } else {
        dispatch(cleanAllDateSelected());
        sessionStorage.setItem('print', JSON.stringify({ general: { category: categoryConfig.id } }));
        history.push(categoryConfig.paths.purchasePrint.stepOne);
      }
    }
  }, [categoryId, operationId, sectionId, typeVehicleId]);

  useEffect(() => {
    if (userMessageData && Object.keys(userMessageData).length === 0) {
      dispatch(fetchUserMessage());
    }
  }, [userMessageData]);

  useEffect(() => {
    if (printData && printData.general && Object.keys(printData.general).length === 0) {
      dispatch(setPrintData(print));
    }
  }, [printData]);

  useEffect(() => {
    if (priceTotalData && Object.keys(priceTotalData).length > 0) {
      setPriceSelected(priceTotalData);
    }
  }, [priceTotalData]);

  useEffect(() => {
    if (extracharges && Object.keys(extracharges).length > 0) {
      setExtraSelected(extracharges);
    }
  }, [extracharges]);

  useEffect(() => {
    if (print && !isNotAnyPrint(printsByChannel) && isClient) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
    }
  }, [printsByChannel]);

  const handleBeforeUnload = (e) => {
    const newPrint = { ...printData };
    newPrint['printsByChannel'] = {};
    newPrint['summaryId'] = '';
    newPrint['extracharges'] = {};
    newPrint['priceTotal'] = {};
    newPrint['photoVignette'] = {};
    newPrint['channelSelected'] = {};
    newPrint['calendar'] = {};
    isClient && sessionStorage.setItem('print', JSON.stringify(newPrint));
    e.returnValue = '¿Desea descartar cambios?';
  };

  const handleClickNext = useCallback(async () => {
    if (isShowChannels) {
      updateDatalayer();
      const updated = getUpdateData();
      dispatch(fetchPrint(printData.general.category, updated));
      setIsNextStep(true);
      isClient && sessionStorage.setItem('print', JSON.stringify(updated));
    } else {
      if (!isNotAnyPrint(printsByChannel)) {
        setIsShowChannels(true);
      }
    }
  }, [publicationTitle, printsByChannel, publications, isShowChannels]);

  const getUpdateData = () => {
    const updated = { ...printData };
    updated['extracharges'] = { ...extracharges };
    updated['priceTotal'] = { ...priceTotal };
    updated['photoVignette'] = { ...photoVignette };
    updated['channelSelected'] = { ...currentChannel };
    updated['calendar'] = { ...currentCalendar };
    return updated;
  };

  const updateDatalayer = () => {
    const selecteds = publicationTitle.join(', ');
    pushDatalayer(categoryConfig.name, selecteds);
    Object.keys(printsByChannel).forEach(key => {
      const channel = publications[key];
      pushDatalayerPayment(categoryConfig.name, key, channel);
    });
  };

  useEffect(() => {
    if (summaryId && isNextStep) {
      history.push(`${categoryConfig.paths.purchasePrint.stepSummary}/${summaryId}`);
      dispatch(clearPrintSummaryId());
    }
  }, [summaryId, isNextStep, categoryConfig]);

  const isNotAnyPrint = (printsByChannel) => {
    let isNotPrint = true;
    let channelsId = printsByChannel && Object.keys(printsByChannel);
    if (channelsId && channelsId.length > 0) {
      channelsId.forEach((key) => {
        isNotPrint = isNotPrint && (printsByChannel[key] && printsByChannel[key].length === 0)
      })
    }
    return isNotPrint;
  }

  const handleClickItem = (idNewspaper) => {
    setIsShowChannels(false);
    setCurrentNewspaper(idNewspaper);
  };

  const setSelectExtraWord = (idChannel) => {
    if (extracharges && extracharges[idChannel] && extracharges[idChannel].data && extracharges[idChannel].data.length > 0) {
      const item = extracharges[idChannel].data.filter((item) => item.alias === EXTRA_WORD_ALIAS)[0];
      dispatch(selectExtracharge(idChannel, item.extrachargeId, true));
    }
  };

  const handleClickOptionDay = (publicationData, publicationDay, idChannel) => {
    dispatch(setTypePublicationSelected(publications[idChannel]));
    if (extraWords) {
      setSelectExtraWord(idChannel);
    }
    const currentTitle = `Clasificados ${publicationData} ${publicationDay}`;
    let isRepeat = false;
    for (let i = 0; i < publicationTitle.length; i++) {
      if (publicationTitle[i] === currentTitle) {
        isRepeat = true;
        break;
      }
    }
    if (publicationTitle.length === 0 || !isRepeat) {
      setPublicationTitle([...publicationTitle, currentTitle]);
    }
    history.push(categoryConfig.paths.purchasePrint.stepCalendar);
  };

  const pushDatalayer = (categoryName: string, publicationName: string) => {
    if (isClient && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        'event': 'EVClasificados',
        'eventCategory': 'Posting',
        'eventAction': `Paso 2.a - ${categoryName}`,
        'eventLabel': publicationName,
        'eventValue': 1
      })
    }
  };

  const pushDatalayerPayment = (categoryName: string, idPublication: string, channel) => {
    const printsTotal = idPublication && printsByChannel && printsByChannel[idPublication].length;
    if (isClient && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        'event': 'EVClasificados',
        'eventCategory': 'Posting',
        'eventAction': `Paso 2 - ${categoryName}`,
        'eventLabel': `Clasificados ${channel.name} ${channel.publicationDay}|${printsTotal}|S/ ${channel.price * printsTotal}`,
        'eventValue': 1
      })
    }
  };

  const handleClickBack = (e) => {
    e.preventDefault();
    if (!isNotAnyPrint(printsByChannel)) {
      const isDiscard = confirm('¿Desea descartar cambios?');
      if (isDiscard) {
        dispatch(clearExtrachargeAll());
        dispatch(cleanAllDateSelected());
        dispatch(clearPhotoVignetteAll());
        dispatch(clearPriceTotal());
        delete printData.printsByChannel;
        isClient && sessionStorage.setItem('print', JSON.stringify(printData));
        history.push(categoryConfig.paths.purchasePrint.stepOne);
      }
    } else {
      isClient && sessionStorage.setItem('print', JSON.stringify(printData));
      history.push(categoryConfig.paths.purchasePrint.stepOne);
    }
  };

  const filterByChannelUnique = (publications) => {
    const currentData = Object.values(publications);
    if (currentData && currentData.length > 0) {
      const arrayUniqueByKey = [...new Map(currentData.map(item =>
        [item['newspaperId'], item])).values()]
      return arrayUniqueByKey;
    }
  };

  const filterByChannel = (publications) => {
    const channelData = Object.values(publications);
    return channelData.filter(item => item['newspaperId'] === currentNewspaper);
  };

  const getPriceByNewspaper = useCallback((idNewspaper) => {
    const obj = { 1: 0, 2: 0, 3: 0 }
    Object.keys(priceSelected).map(key => {
      newspaperIdGroup.map(id => {
        if (configCalendarId[key] && configCalendarId[key].groupId === id) {
          obj[id] = obj[id] + priceSelected[key].price
        }
      })
    });
    return formatPrice(obj[idNewspaper]);
  }, [priceSelected]);

  const getPrintsByNewspaperId = (printsByChannel, id) => {
    const obj = { 1: [], 2: [], 3: [] }
    Object.keys(printsByChannel).map(key => {
      newspaperIdGroup.map(id => {
        if (configCalendarId[key] && configCalendarId[key].groupId === id) {
          obj[id] = [
            ...obj[id],
            ...printsByChannel[key]
          ];
        }
      })
    });
    return obj[id];
  };

  const getExtraByNewspaperId = (extracharge, id) => {
    const obj = { 1: [], 2: [], 3: [] }
    Object.keys(extracharge).map(key => {
      newspaperIdGroup.map(id => {
        if (extracharge && configCalendarId[key] && configCalendarId[key].groupId === id && extracharge[key].data) {
          obj[id] = [
            ...obj[id],
            ...extracharge[key].data
          ];
        }
      })
    });
    return obj[id].filter(item => (item.checked === true));
  };

  return (
    <Wrapper>
      <Title>¿Dónde quieres publicar?</Title>
      <SubTitle>impresos</SubTitle>
      <List>
        {
          (isFetching || error) ?
            <ChannelsPublicationShimmer />
            :
            isShowChannels && publications && Object.keys(publications).length > 0 && filterByChannelUnique(publications) &&
            filterByChannelUnique(publications).map((channel: TypePublicationModel, key) => {
              return (
                <React.Fragment key={key}>
                  <OptionChannelSummary
                    key={key}
                    idNewspaper={channel.newspaperId}
                    title={channel.name}
                    priceIgv={channel.priceIgv}
                    details={printsByChannel && getPrintsByNewspaperId(printsByChannel, channel.newspaperId)}
                    handleClickItem={() => handleClickItem(channel.newspaperId)}
                    priceSubTotal={priceSelected && getPriceByNewspaper(channel.newspaperId)}
                    extrachargeSelected={extraSelected && getExtraByNewspaperId(extraSelected, channel.newspaperId)}
                  />
                </React.Fragment>
              )
            })
        }
        {
          !isShowChannels && publications && Object.keys(publications).length > 0 && filterByChannel(publications) &&
          filterByChannel(publications).map((channel: TypePublicationModel, key) => {
            return (
              <OptionChannel
                key={key}
                idChannel={channel.id}
                title={channel.name}
                day={channel.publicationDay}
                priceIgv={channel.priceIgv}
                details={printsByChannel && printsByChannel[channel.id]}
                printData={channel}
                handleClickItem={() => handleClickOptionDay(channel.name, channel.publicationDay, channel.id)}
                priceSubTotal={formatPrice(priceTotalData && priceTotalData[channel.id] && priceTotalData[channel.id].price)}
                extra={extracharges}
              />
            )
          })
        }
      </List>
      <FormActions
        textButtonBack={'Datos del aviso'}
        handleButtonBack={handleClickBack}
        handleButtonNext={handleClickNext}
        disabledButtonNext={isNotAnyPrint(printsByChannel) || printIsLoading} />
      <ModalErrorFeature />
    </Wrapper>
  );
};
